import { Suspense, lazy, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiFetch, clearToken } from './api';
import { Dashboard, MyHoursWidget } from './Dashboard';
import { DealPicker, type Deal } from './DealPicker';
import { RecentEntries } from './RecentEntries';
import { SessionLog } from './SessionLog';
import { SplitDay } from './components/SplitDay';
import { PageShell } from './components/PageShell';
import { RouteSkeleton } from './components/RouteSkeleton';
import { dealLabel } from './lib/dealLabel';

// Supervisor-only admin routes are code-split so the ~1.6k LOC of admin
// TSX (Approve / AdminJobs / BillingSummary / HubspotQueue / Payroll /
// Rates) isn't downloaded by the four daily non-supervisor users.
const AdminJobs = lazy(() => import('./AdminJobs').then((m) => ({ default: m.AdminJobs })));
const Approve = lazy(() => import('./Approve').then((m) => ({ default: m.Approve })));
const BillingSummary = lazy(() =>
  import('./BillingSummary').then((m) => ({ default: m.BillingSummary })),
);
const HubspotQueue = lazy(() => import('./HubspotQueue').then((m) => ({ default: m.HubspotQueue })));
const Payroll = lazy(() => import('./Payroll').then((m) => ({ default: m.Payroll })));
const Rates = lazy(() => import('./Rates').then((m) => ({ default: m.Rates })));
// /week is a personal logging surface (not admin-gated) but still code-split
// so the grid + inline editor aren't in the initial bundle.
const Week = lazy(() => import('./Week').then((m) => ({ default: m.Week })));
// The same board embeds on the home page (Tim's trial feedback: submissions
// should be visible where they went, with a prev/next week toggle). Lazy for
// the same bundle reason; Vite dedupes the chunk with the /week route.
const WeekBoard = lazy(() =>
  import('./components/week/WeekBoard').then((m) => ({ default: m.WeekBoard })),
);

// PR 0007 — per-user identity. The server resolves identity from the
// Cloudflare Access JWT; the SPA learns who's logged in via /api/me.
// The pre-PR "Who's logging" picker is gone — Fred can only ever act as
// Fred, Tim can only ever act as Tim. Supervisor admin views are still
// gated by me.isSupervisor.
interface Me {
  id: string;
  fullName: string;
  email: string;
  isSupervisor: boolean;
  source: 'employee' | 'support';
  flatBillingTypes: string[];
  prefersTimeRange: boolean;
}

const DEAL_KEY = 'capstone:dealId';

// Update or create a meta tag in <head>. Used by the path-tracking
// effect to keep og:title / og:url / twitter:title in sync with the
// active route.
function setMeta(keyAttr: 'name' | 'property', keyValue: string, content: string): void {
  let el = document.querySelector(`meta[${keyAttr}="${keyValue}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(keyAttr, keyValue);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

// Map a SPA pathname to a human slug for document.title + og:title.
// Empty string for the home route -> just "Capstone Time Sheet" with no
// pipe-suffix. Unknown paths fall back to a humanized last segment.
function slugForPath(path: string): string {
  switch (path) {
    case '/':
      return 'Home';
    case '/approve':
      return 'Approvals';
    case '/week':
      return 'Week';
    case '/admin/jobs':
      return 'Jobs';
    case '/admin/billing-summary':
      return 'Billing';
    case '/admin/payroll':
      return 'Payroll';
    case '/admin/rates':
      return 'Rates';
    case '/admin/hubspot':
      return 'HubSpot Queue';
    default: {
      const last = path.split('/').filter(Boolean).pop();
      if (!last) return '';
      return last.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    }
  }
}

export function App() {
  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: () => apiFetch<Me>('/me'),
    retry: false,
  });
  const dealsQuery = useQuery({
    queryKey: ['deals'],
    queryFn: () => apiFetch<Deal[]>('/deals'),
    enabled: meQuery.isSuccess,
  });

  const [dealId, setDealId] = useState<string>(() => localStorage.getItem(DEAL_KEY) ?? '');
  const [path, setPath] = useState<string>(() => window.location.pathname);
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);
  // Tim's trial feedback: after submitting hours the form must reset
  // completely, job included. `justLogged` keeps the auto-select effect
  // from instantly re-filling the picker and swaps the "pick a job" copy
  // for a confirmation so the submit doesn't feel like it vanished.
  const [justLogged, setJustLogged] = useState<boolean>(false);

  useEffect(() => {
    if (dealId) localStorage.setItem(DEAL_KEY, dealId);
  }, [dealId]);
  useEffect(() => {
    const first = dealsQuery.data?.[0];
    if (!dealId && !justLogged && first) setDealId(first.id);
  }, [dealId, justLogged, dealsQuery.data]);

  const clearDealAfterLog = (): void => {
    localStorage.removeItem(DEAL_KEY);
    setDealId('');
    setJustLogged(true);
  };

  useEffect(() => {
    const handle = (): void => setPath(window.location.pathname);
    window.addEventListener('popstate', handle);
    return () => window.removeEventListener('popstate', handle);
  }, []);

  // Update document.title, og:title, twitter:title, og:url on route
  // change so each section shows its own slug in the browser tab and in
  // any link previews. Static index.html keeps a generic title for the
  // initial paint; this hook takes over after hydration.
  useEffect(() => {
    const slug = slugForPath(path);
    const title = slug ? `Capstone Time Sheet | ${slug}` : 'Capstone Time Sheet';
    document.title = title;
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:url', `https://time.capstonecreates.com${path}`);
    setMeta('name', 'twitter:title', title);
  }, [path]);

  const navigate = (p: string): void => {
    window.history.pushState({}, '', p);
    setPath(p);
    setMobileMenuOpen(false);
  };

  const signOut = (): void => {
    clearToken();
    window.location.reload();
  };

  const me = meQuery.data;
  const deals = dealsQuery.data ?? [];
  const selectedDeal = deals.find((d) => d.id === dealId);
  const isSupervisor = me?.isSupervisor === true;
  // Support identities (Ryan) are supervisor but don't have an employees
  // row, so they can't log time. They get the supervisor admin views and
  // a "no logging surface" banner.
  const canLogTime = me?.source === 'employee';

  if (meQuery.isLoading) {
    return (
      <PageShell
        title={<h1 className="text-2xl font-bold tracking-tight text-brand-white">Capstone Engineering</h1>}
        headerAside={null}
      >
        <p className="py-8 text-sm text-brand-slate">Resolving identity…</p>
      </PageShell>
    );
  }

  if (meQuery.isError || !me) {
    return (
      <PageShell
        title={<h1 className="text-2xl font-bold tracking-tight text-brand-white">Capstone Engineering</h1>}
        headerAside={null}
      >
        <div className="mx-auto max-w-md py-8">
          <p className="rounded-lg bg-red-50 p-4 text-sm text-red-700 ring-1 ring-red-200">
            We couldn't verify who you are. Reload the page; if it still fails, your email may not be set
            up as a Capstone employee yet — text Tim.
          </p>
        </div>
      </PageShell>
    );
  }

  const adminRoute = ((): JSX.Element | null => {
    if (!isSupervisor) return null;
    switch (path) {
      case '/approve':
        return <Approve onBack={() => navigate('/')} />;
      case '/admin/jobs':
        return <AdminJobs onBack={() => navigate('/')} />;
      case '/admin/billing-summary':
        return <BillingSummary onBack={() => navigate('/')} />;
      case '/admin/hubspot':
        return <HubspotQueue onBack={() => navigate('/')} />;
      case '/admin/payroll':
        return <Payroll onBack={() => navigate('/')} />;
      case '/admin/rates':
        return <Rates onBack={() => navigate('/')} supervisorId={me.id} />;
      default:
        return null;
    }
  })();
  if (adminRoute) {
    return <Suspense fallback={<RouteSkeleton />}>{adminRoute}</Suspense>;
  }

  // /week is available to anyone who can log time (employees + supervisors with
  // an employee row). Support identities (no employee row) fall through to home.
  if (path === '/week' && canLogTime) {
    return (
      <Suspense fallback={<RouteSkeleton />}>
        <Week
          onBack={() => navigate('/')}
          employeeId={me.id}
          flatBillingTypes={me.flatBillingTypes}
          isSupervisor={isSupervisor}
        />
      </Suspense>
    );
  }

  const navLinkClass =
    'rounded-lg px-3 py-2 text-sm text-brand-white/80 ring-1 ring-brand-white/20 hover:bg-white/10';
  // Mobile menu items: tighter than desktop links (2-column grid, no
  // full-width buttons). Same hover state; smaller padding + centered text.
  const mobileNavLinkClass =
    'rounded-lg px-3 py-2.5 text-center text-sm font-medium text-brand-white/90 ring-1 ring-brand-white/15 hover:bg-white/10 active:scale-95 transition-transform';

  const title = (
    <>
      <h1 className="text-2xl font-bold tracking-tight">
        <span className="text-brand-white">Capstone</span>
        <span className="hidden text-brand-blue sm:inline"> Engineering</span>
      </h1>
      {isSupervisor && (
        <span className="rounded-full bg-brand-blue/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-brand-blue ring-1 ring-brand-blue/40">
          Admin
        </span>
      )}
    </>
  );

  const adminLinks: { href: string; label: string }[] = [
    { href: '/week', label: 'Week' },
    { href: '/admin/jobs', label: 'Jobs' },
    { href: '/admin/billing-summary', label: 'Billing' },
    { href: '/admin/payroll', label: 'Payroll' },
    { href: '/admin/rates', label: 'Rates' },
    { href: '/admin/hubspot', label: 'Queue' },
    { href: '/approve', label: 'Approvals' },
  ];

  const signOutButton = (
    <button
      type="button"
      onClick={signOut}
      className="whitespace-nowrap rounded-lg px-3 py-2 text-left text-sm text-brand-white/80 ring-1 ring-brand-white/20 hover:bg-white/10"
    >
      Sign out
    </button>
  );

  const identityBadge = (
    <span className="rounded-lg bg-brand-charcoal/40 px-3 py-2 text-sm text-brand-white/80 ring-1 ring-brand-white/20">
      <span className="text-brand-white/60">Signed in as </span>
      <span className="font-semibold text-brand-white">{me.fullName}</span>
    </span>
  );

  const headerAside = isSupervisor ? (
    <>
      <nav className="hidden flex-wrap gap-2 sm:flex" aria-label="Admin sections">
        {adminLinks.map((link) => (
          <a
            key={link.href}
            href={link.href}
            onClick={(e) => {
              e.preventDefault();
              navigate(link.href);
            }}
            className={navLinkClass}
          >
            {link.label}
          </a>
        ))}
      </nav>
      <div className="hidden sm:block">{identityBadge}</div>
      <div className="hidden sm:block">{signOutButton}</div>
      <button
        type="button"
        onClick={() => setMobileMenuOpen((open) => !open)}
        aria-expanded={mobileMenuOpen}
        aria-controls="mobile-admin-nav"
        aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
        className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-brand-white ring-1 ring-brand-white/20 hover:bg-white/10 sm:hidden"
      >
        {mobileMenuOpen ? (
          <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true" fill="none">
            <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        ) : (
          <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true" fill="none">
            <path d="M3 6h14M3 10h14M3 14h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        )}
      </button>
    </>
  ) : (
    <>
      <div className="hidden sm:block">{identityBadge}</div>
      {signOutButton}
    </>
  );

  // Mobile menu panel rendered as a PageShell subHeader (a separate row
  // inside the navbar's dark background) instead of as a sibling of the
  // hamburger button. Keeping it out of the headerAside flex group is
  // what stops the w-full panel from wrapping the title row and breaking
  // the hamburger's vertical alignment.
  const mobileMenuPanel = isSupervisor ? (
    <nav
      id="mobile-admin-nav"
      aria-label="Admin sections"
      aria-hidden={!mobileMenuOpen}
      className={
        'overflow-hidden transition-[max-height,opacity] duration-300 ease-out sm:hidden ' +
        (mobileMenuOpen ? 'max-h-[480px] opacity-100' : 'pointer-events-none max-h-0 opacity-0')
      }
    >
      <div className="flex flex-col gap-3 pb-4 pt-1">
        <div className="grid grid-cols-2 gap-2">
          {adminLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              tabIndex={mobileMenuOpen ? 0 : -1}
              onClick={(e) => {
                e.preventDefault();
                navigate(link.href);
              }}
              className={mobileNavLinkClass}
            >
              {link.label}
            </a>
          ))}
        </div>
        <div className="flex items-center justify-between gap-2 border-t border-brand-white/10 pt-2 text-xs">
          <span className="text-brand-white/60">
            Signed in as <span className="font-semibold text-brand-white">{me.fullName}</span>
          </span>
          <button
            type="button"
            tabIndex={mobileMenuOpen ? 0 : -1}
            onClick={signOut}
            className="rounded-lg px-2.5 py-1.5 text-xs text-brand-white/80 ring-1 ring-brand-white/20 hover:bg-white/10"
          >
            Sign out
          </button>
        </div>
      </div>
    </nav>
  ) : null;

  return (
    <PageShell title={title} headerAside={headerAside} subHeader={mobileMenuPanel}>
      <div className="flex flex-col gap-4 py-4">
        {/* PR 0010 — surfaced above the two-column layout so "Hours this
            period" is the first thing visible after the navbar. The
            widget shares the /dashboard React Query cache with the
            Dashboard below; no extra round trip. */}
        <MyHoursWidget isSupervisor={isSupervisor} />
      </div>
      <div className="flex flex-col gap-4 pb-4 lg:grid lg:grid-cols-[minmax(320px,400px)_1fr] lg:items-start lg:gap-6">
        <div className="flex flex-col gap-4 lg:sticky lg:top-4 lg:self-start">
          <section className="flex flex-col gap-3 sm:grid sm:grid-cols-2 sm:gap-3 lg:grid-cols-1">
            <div className="flex flex-col gap-1">
              <span className="text-xs font-semibold uppercase tracking-wide text-brand-charcoal">
                Job
              </span>
              {deals.length === 0 ? (
                <div className="min-h-[44px] rounded-lg bg-brand-steel px-3 py-2 text-sm text-brand-slate ring-1 ring-brand-steel">
                  No active deals — run import:hubspot
                </div>
              ) : (
                <DealPicker
                  deals={deals}
                  value={dealId || null}
                  onChange={(id) => {
                    setDealId(id);
                    setJustLogged(false);
                  }}
                />
              )}
            </div>
          </section>

          {canLogTime && (
            <a
              href="/week"
              onClick={(e) => {
                e.preventDefault();
                navigate('/week');
              }}
              className="flex min-h-[44px] items-center justify-between rounded-lg bg-brand-steel px-3 py-2 text-sm font-medium text-brand-charcoal ring-1 ring-brand-steel hover:bg-brand-mist"
            >
              <span>View this week</span>
              <span aria-hidden>→</span>
            </a>
          )}

          {!canLogTime ? (
            <p className="rounded-xl bg-brand-steel p-4 text-sm text-brand-slate ring-1 ring-brand-steel">
              You're signed in as a support user, not a Capstone employee — no time-logging surface for
              you. Use the supervisor views above.
            </p>
          ) : selectedDeal ? (
            <SessionLog
              dealId={selectedDeal.id}
              employeeId={me.id}
              dealLabel={dealLabel(selectedDeal)}
              flatBillingTypes={me.flatBillingTypes}
              prefersTimeRange={me.prefersTimeRange}
              onLogged={clearDealAfterLog}
            />
          ) : justLogged ? (
            <p className="rounded-lg bg-emerald-50 p-3 text-sm text-emerald-800 ring-1 ring-emerald-200">
              Hours logged. Pick a job to log more.
            </p>
          ) : (
            <p className="text-sm text-brand-slate">Pick a job to start logging.</p>
          )}

          {/* Tim's final feedback item 2 (Fred's many-small-jobs days):
              split a day's total hours evenly across several jobs at once.
              Independent of the single-job picker above. */}
          {canLogTime && <SplitDay employeeId={me.id} deals={deals} />}
        </div>

        <div className="flex min-w-0 flex-col gap-4">
          {canLogTime && (
            <section className="flex flex-col gap-3 pt-4">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-brand-charcoal">
                This week
              </h2>
              <Suspense fallback={null}>
                <WeekBoard
                  employeeId={me.id}
                  flatBillingTypes={me.flatBillingTypes}
                  isSupervisor={isSupervisor}
                />
              </Suspense>
            </section>
          )}
          <Dashboard />
          {canLogTime && (
            <RecentEntries
              employeeId={me.id}
              flatBillingTypes={me.flatBillingTypes}
              isSupervisor={isSupervisor}
            />
          )}
        </div>
      </div>
    </PageShell>
  );
}
